import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { v: "awaiting_bank_payment", l: "Awaiting Bank Payment" },
  { v: "paid_processing", l: "Paid / Processing" },
  { v: "shipped", l: "Shipped" },
  { v: "completed", l: "Completed" },
  { v: "cancelled", l: "Cancelled" },
];
const emptyAddr = { line1: "", line2: "", city: "", region: "", postcode: "", country: "New Zealand" };

// Shared line-item editor used by both manual create and edit dialogs.
export function ItemsEditor({ products, items, setItems }) {
  const addItem = () => setItems([...items, { product_id: "", sku: "", name: "", unit_price: "", qty: 1 }]);
  const upd = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const pick = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    setItems(items.map((it, idx) => idx === i ? {
      ...it, product_id: pid, sku: p?.sku || "", name: p?.name || "",
      unit_price: p ? p.final_customer_price : it.unit_price,
    } : it));
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <Label>Line Items</Label>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`order-item-row-${i}`}>
          <div className="col-span-5">
            <Select value={it.product_id || "custom"} onValueChange={(v) => v === "custom" ? upd(i, "product_id", "") : pick(i, v)}>
              <SelectTrigger data-testid={`order-item-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="custom">Custom line…</SelectItem>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} · {p.name} (stk {p.stock})</SelectItem>)}
              </SelectContent>
            </Select>
            {!it.product_id && (
              <Input className="mt-1" placeholder="Custom item name" value={it.name} onChange={(e) => upd(i, "name", e.target.value)} />
            )}
          </div>
          <Input className="col-span-2" type="number" placeholder="Qty" value={it.qty} data-testid={`order-item-qty-${i}`} onChange={(e) => upd(i, "qty", e.target.value)} />
          <Input className="col-span-4" type="number" step="0.01" placeholder="Unit price incl GST" value={it.unit_price} data-testid={`order-item-price-${i}`} onChange={(e) => upd(i, "unit_price", e.target.value)} />
          <button className="col-span-1 text-muted-foreground hover:text-destructive" onClick={() => remove(i)}><Trash2 size={16} /></button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="order-add-item"><Plus size={14} className="mr-1" /> Add item</Button>
    </div>
  );
}

export function AddressFields({ label, addr, onChange, prefix }) {
  const set = (k, v) => onChange({ ...addr, [k]: v });
  return (
    <div>
      <Label className="font-semibold">{label}</Label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <Input className="col-span-2" placeholder="Address line 1" value={addr.line1} data-testid={`${prefix}-line1`} onChange={(e) => set("line1", e.target.value)} />
        <Input placeholder="City" value={addr.city} data-testid={`${prefix}-city`} onChange={(e) => set("city", e.target.value)} />
        <Input placeholder="Region" value={addr.region} onChange={(e) => set("region", e.target.value)} />
        <Input placeholder="Postcode" value={addr.postcode} onChange={(e) => set("postcode", e.target.value)} />
        <Input placeholder="Country" value={addr.country} data-testid={`${prefix}-country`} onChange={(e) => set("country", e.target.value)} />
      </div>
    </div>
  );
}

export { STATUSES, emptyAddr };

export default function ManualOrderForm({ products, onDone }) {
  const [c, setC] = useState({ name: "", company: "", email: "", phone: "" });
  const [billing, setBilling] = useState({ ...emptyAddr });
  const [delivery, setDelivery] = useState({ ...emptyAddr });
  const [items, setItems] = useState([{ product_id: "", sku: "", name: "", unit_price: "", qty: 1 }]);
  const [shippingOverride, setShippingOverride] = useState("");
  const [status, setStatus] = useState("awaiting_bank_payment");
  const [deductStock, setDeductStock] = useState(true);
  const [sendEmails, setSendEmails] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!c.name || !c.email) return toast.error("Customer name and email are required");
    if (!items.some((i) => i.qty)) return toast.error("Add at least one item");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders", {
        ...c, billing_address: billing, delivery_address: delivery,
        items: items.map((i) => ({
          product_id: i.product_id || null, sku: i.sku || null, name: i.name || null,
          unit_price: i.unit_price === "" ? null : Number(i.unit_price), qty: Number(i.qty || 1),
        })),
        shipping_override: shippingOverride === "" ? null : Number(shippingOverride),
        status, deduct_stock: deductStock, send_emails: sendEmails, payment_method: "bank_transfer",
      });
      toast.success(`Order ${data.order_number} created`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create order"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Customer Name *</Label><Input data-testid="mo-name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} /></div>
        <div><Label>Company</Label><Input value={c.company} onChange={(e) => setC({ ...c, company: e.target.value })} /></div>
        <div><Label>Email *</Label><Input data-testid="mo-email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} /></div>
        <div><Label>Phone</Label><Input value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} /></div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <AddressFields label="Billing Address" addr={billing} onChange={setBilling} prefix="mo-bill" />
        <AddressFields label="Delivery Address" addr={delivery} onChange={setDelivery} prefix="mo-del" />
      </div>
      <ItemsEditor products={products} items={items} setItems={setItems} />
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Shipping Override (blank = auto)</Label><Input data-testid="mo-shipping" type="number" step="0.01" value={shippingOverride} onChange={(e) => setShippingOverride(e.target.value)} placeholder="auto" /></div>
        <div><Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="mo-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-6 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={deductStock} onChange={(e) => setDeductStock(e.target.checked)} data-testid="mo-deduct" /> Reduce stock (allows any level)</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={sendEmails} onChange={(e) => setSendEmails(e.target.checked)} /> Send confirmation emails</label>
      </div>
      <Button data-testid="mo-submit" onClick={submit} disabled={busy} className="w-full bg-accent text-steel hover:bg-amber-600 font-semibold">
        {busy ? "Creating…" : "Raise Order"}
      </Button>
    </div>
  );
}
